import {Button, Divider, Icon, Input} from 'antd';
import copy from 'copy-to-clipboard';
import {inject, observer} from 'mobx-react';
import * as React from 'react';
import {Helmet} from 'react-helmet';
import ReactMapGL, {Marker, NavigationControl} from 'react-map-gl';
import {RouteComponentProps} from 'react-router';
import Card, {CardBody, CardHeader} from '../../components/Card';
import Flex from '../../components/Flex';
import Layout, {Main} from '../../components/Layout';
import Text from '../../components/Text';
import {Auth} from '../../models/authModel';
import {
  ListingModelType,
  ListingsListModelType
} from '../../models/ListingsListModel';
import styled from '../../utils/styled';
import './index.css';

const MAPBOX_TOKEN =
  // tslint:disable-next-line: no-any
  (window as any).MAPBOX_TOKEN || process.env.REACT_APP_MAPBOX_TOKEN;

// tslint:disable
interface WithInjectedProps extends RouteComponentProps<{listingId: string}> {
  listingsList: ListingsListModelType;
  auth: Auth;
}

interface MapBoxState {
  viewport: {
    height: number | string;
    width: number | string;
    zoom: number;
  };
}

const ListingAttribute = styled(Flex)({
  boxSizing: 'border-box',
  padding: '0 20px',
  width: '100%',
  minHeight: 40,
  borderRight: '1px solid #F1F3F6',
  justifyContent: 'space-between'
});

const ListingImage = styled('img')({
  width: 100,
  height: 100,
  padding: 5,
  borderRadius: 3,
  display: 'inline-block'
});

@inject('listingsList', 'auth')
@observer
class LongTermListingDetail extends React.Component<
  RouteComponentProps,
  MapBoxState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      viewport: {
        height: 400,
        width: '80%',
        zoom: 12
      }
    };
  }

  componentDidMount() {
    this._withInjectedProps.listingsList.loadEntry(this._listingId);
  }

  // componentWillUnmount() {
  //   window.removeEventListener('resize', this.resize);
  // }

  public updateViewport = (viewport: any) => {
    this.setState(prevState => ({
      viewport: {...prevState.viewport, ...viewport}
    }));
  };

  private get _listingId() {
    return Number(this._withInjectedProps.match.params.listingId);
  }

  private get _withInjectedProps() {
    return this.props as WithInjectedProps;
  }

  render() {
    const {
      isListingLoading: isLoading,
      currentListing: listing
      // isListingLoaded: isLoaded
    } = this._withInjectedProps.listingsList;

    return (
      <React.Fragment>
        <Helmet>
          <title>Listing Detail</title>
        </Helmet>
        <Layout>
          <Main>
            <Card>
              <CardHeader>Listing Detail</CardHeader>
              {isLoading
                ? this._loading()
                : this._detailListing(listing as ListingModelType)}
            </Card>
          </Main>
        </Layout>
      </React.Fragment>
    );
  }

  private _detailListing(listing: ListingModelType) {
    return listing ? (
      <React.Fragment>
        <CardBody>
          <ListingAttribute>
            <Text color="primary" style={{width: '15%'}}>
              Name
            </Text>
            <Text style={{width: '80%'}}>{listing.name}</Text>
          </ListingAttribute>

          <ListingAttribute>
            <Text color="primary" style={{width: '15%'}}>
              Address
            </Text>
            <Text style={{width: '80%'}}>{listing.address}</Text>
          </ListingAttribute>

          <ListingAttribute>
            <Text color="primary" style={{width: '15%'}}>
              Internal name
            </Text>
            <Text style={{width: '80%'}}>{listing.internalName}</Text>
          </ListingAttribute>

          <ListingAttribute>
            <Text color="primary" style={{width: '15%'}}>
              Images
            </Text>

            <Flex
              style={{
                display: 'block',
                width: '80%',
                maxHeight: 220,
                overflow: 'scroll'
              }}
            >
              {// tslint:disable-next-line:no-any
              listing.images.map(image => (
                <ListingImage
                  src={image.url || image.small}
                  key={image.url || image.small}
                />
              ))}
            </Flex>
          </ListingAttribute>

          {listing.longTermVariant ? (
            <React.Fragment>
              <Divider />

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Beds
                </Text>
                <Text style={{width: '80%'}}>
                  {listing.longTermVariant.beds}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Baths
                </Text>
                <Text style={{width: '80%'}}>
                  {listing.longTermVariant.baths}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Area
                </Text>
                <Text style={{width: '80%'}}>
                  {`${listing.longTermVariant.area} ${
                    listing.longTermVariant.areaUnit
                  }`}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Daily rate
                </Text>
                <Text style={{width: '80%'}}>
                  {`${listing.longTermVariant.dailyRate} ${
                    listing.longTermVariant.dailyCurrency
                  }`}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Monthly rate
                </Text>
                <Text style={{width: '80%'}}>
                  {`${listing.longTermVariant.monthlyRate} ${
                    listing.longTermVariant.monthlyCurrency
                  }`}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Parking
                </Text>
                <Text style={{width: '80%'}}>
                  {listing.longTermVariant.parking}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Requested market
                </Text>

                <Text style={{width: '80%'}}>
                  {listing.longTermVariant.marketRequest}
                </Text>
              </ListingAttribute>

              <ListingAttribute>
                <Text color="primary" style={{width: '15%'}}>
                  Map
                </Text>

                <ReactMapGL
                  {...this.state.viewport}
                  latitude={listing.longTermVariant.latitude}
                  longitude={listing.longTermVariant.longitude}
                  mapboxApiAccessToken={MAPBOX_TOKEN}
                >
                  <div style={{position: 'absolute', right: 30, bottom: 30}}>
                    <NavigationControl
                      onViewportChange={this.updateViewport}
                    />
                  </div>
                  <Marker
                    draggable={true}
                    latitude={listing.longTermVariant.latitude}
                    longitude={listing.longTermVariant.longitude}
                  >
                    <Icon
                      type="environment"
                      style={{color: 'red', fontSize: '30px'}}
                    />
                  </Marker>
                </ReactMapGL>
              </ListingAttribute>

              <ListingAttribute style={{paddingTop: 20}}>
                <Text color="primary" style={{width: '15%'}}>
                  Title
                </Text>
                {listing.longTermVariant.titleContent ? (
                  <Flex style={{width: '80%'}} justify="space-between">
                    <Input.TextArea
                      style={{width: '92%'}}
                      readOnly
                      autosize={{minRows: 1, maxRows: 2}}
                      value={listing.longTermVariant.titleContent.renderedText}
                    />
                    <Button
                      onClick={() => {
                        if (
                          listing &&
                          listing.longTermVariant &&
                          listing.longTermVariant.titleContent
                        ) {
                          copy(
                            listing.longTermVariant.titleContent.renderedText
                          );
                        }
                      }}
                    >
                      <Icon type="copy" />
                    </Button>
                  </Flex>
                ) : (
                  <div style={{width: '80%'}}>Content</div>
                )}
              </ListingAttribute>

              <ListingAttribute style={{paddingTop: 20}}>
                <Text color="primary" style={{width: '15%'}}>
                  Description
                </Text>
                {listing.longTermVariant.descriptionContent ? (
                  <Flex style={{width: '80%'}} justify="space-between">
                    <Input.TextArea
                      style={{width: '92%'}}
                      readOnly
                      autosize={{minRows: 2, maxRows: 10}}
                      value={
                        listing.longTermVariant.descriptionContent.renderedText
                      }
                    />

                    <Button
                      onClick={() => {
                        if (
                          listing &&
                          listing.longTermVariant &&
                          listing.longTermVariant.descriptionContent
                        ) {
                          copy(
                            listing.longTermVariant.descriptionContent
                              .renderedText
                          );
                        }
                      }}
                    >
                      <Icon type="copy" />
                    </Button>
                  </Flex>
                ) : (
                  <div style={{width: '80%'}}>No description</div>
                )}
              </ListingAttribute>
            </React.Fragment>
          ) : null}
        </CardBody>
      </React.Fragment>
    ) : null;
  }

  private _loading() {
    return (
      <div style={{margin: 'auto', minHeight: 300}}>
        <Button shape="circle" type="primary" loading={true} />
      </div>
    );
  }
}

export default LongTermListingDetail;



// WEBPACK FOOTER //
// ./src/pages/LongTermListingDetail/LongTermListingDetail.tsx